import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play, Trash2, Pause, CalendarClock, RefreshCw } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const cadenceText = (s) => {
  if (s.frequency === "daily") return `Daily ${s.time} IST`;
  if (s.frequency === "monthly") return `Monthly on day ${s.day_of_month}, ${s.time} IST`;
  return `Every ${WEEKDAYS[s.weekday] || "Mon"} ${s.time} IST`;
};

export const ScheduledReports = ({ open }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.chatSchedules().then((r) => setItems(r.data?.schedules || [])).catch(() => {});
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const runNow = async (id) => {
    setBusy(id);
    try {
      await api.chatScheduleRunNow(id);
      toast.success("Running now — status will update below in ~1 min");
      setTimeout(load, 20000);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };

  const toggle = async (id) => {
    try { await api.chatScheduleToggle(id); load(); } catch { }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this scheduled report?")) return;
    try { await api.chatScheduleDelete(id); load(); } catch { }
  };

  return (
    <div className="border-b border-[#E2E5EA] bg-white max-h-[280px] overflow-y-auto" data-testid="scheduled-reports-panel">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[#F1F3F5] sticky top-0 bg-white">
        <CalendarClock size={13} className="text-[#E32C17]" />
        <span className="text-[10px] font-bold uppercase tracking-wider">Scheduled Reports</span>
        <button onClick={load} className="ml-auto p-1 hover:bg-[#F1F3F5] rounded-sm text-[#6B7280]" title="Refresh" data-testid="schedules-refresh-btn">
          <RefreshCw size={12} />
        </button>
      </div>
      {items.length === 0 ? (
        <div className="p-3 text-[10px] text-[#9CA3AF] text-center">
          No scheduled reports yet — ask for a report in chat, then click the clock icon on the table
        </div>
      ) : items.map((s) => (
        <div key={s.id} className="px-4 py-2 border-b border-[#F1F3F5] flex items-center gap-2" data-testid={`schedule-row-${s.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium text-[#1A1D21] truncate">{s.title}</div>
            <div className="text-[9px] text-[#6B7280]">
              {cadenceText(s)} · {(s.formats || []).join("+").toUpperCase()} → {(s.recipients || []).join(", ")}
            </div>
            {s.last_status && (
              <div className={`text-[9px] ${s.last_status === "sent" ? "text-emerald-600" : "text-red-600"}`}>
                Last run: {s.last_status === "sent" ? "✓ sent" : `✗ ${s.last_error || "error"}`}
              </div>
            )}
          </div>
          {!s.active && <span className="text-[8px] font-bold uppercase bg-[#F1F3F5] text-[#6B7280] px-1.5 py-0.5 rounded-sm">Paused</span>}
          <button onClick={() => runNow(s.id)} disabled={busy === s.id} title="Send now"
            className="p-1 hover:bg-emerald-50 text-emerald-600 rounded-sm disabled:opacity-40" data-testid={`schedule-run-${s.id}`}>
            <Play size={12} />
          </button>
          <button onClick={() => toggle(s.id)} title={s.active ? "Pause" : "Resume"}
            className="p-1 hover:bg-[#F1F3F5] text-[#6B7280] rounded-sm" data-testid={`schedule-toggle-${s.id}`}>
            <Pause size={12} />
          </button>
          <button onClick={() => del(s.id)} title="Delete"
            className="p-1 hover:bg-red-50 text-red-500 rounded-sm" data-testid={`schedule-delete-${s.id}`}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  );
};
